import * as React from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

import { handleArrowDown, handleArrowUp, handleEnter } from '../Utils/events/events';

import { generateId } from './generateId';
export interface IItem {
  label: string;
  value: string;
  group?: string;
}
export interface IListboxState {
  // array of index for selected items
  selected: number[];
  // currently "focused" item
  folkused: number;
}

export enum ActionTypes {
  select = 'SELECT',
  unselect = 'UNSELECT',
  folkus = 'FOLKUS',
}

// tslint:disable no-reserved-keywords
export type ListboxActions =
  | { type: ActionTypes.select; payload: number }
  | { type: ActionTypes.unselect; payload: number }
  | { type: ActionTypes.folkus; payload: number };
// tslint:enable no-reserved-keywords

export type ListboxReducer = React.Reducer<IListboxState, ListboxActions>;

export const listboxReducer = (
  prevState: IListboxState,
  action: ListboxActions
): IListboxState => {
  switch (action.type) {
    case ActionTypes.select:
      if (!prevState.selected.includes(action.payload) && action.payload >= 0) {
        prevState.selected.push(action.payload);
      }

      return { selected: prevState.selected, folkused: prevState.folkused };

    case ActionTypes.unselect:
      prevState.selected.splice(action.payload, 1);

      return { selected: prevState.selected, folkused: prevState.folkused };

    case ActionTypes.folkus:

      return { selected: prevState.selected, folkused: action.payload };

    default:

      return prevState;
  }
};

export const useListbox = (
    items: IItem[],
    selected: number[] = [],
    customReducer: ListboxReducer = listboxReducer,
    onSelect?: (index: number) => void
  ) => {

  const ids = React.useRef(items.map(() => generateId('option')));

  const [listboxState, dispatch] = React.useReducer(customReducer, { selected, folkused: -1 });

  const folkusedNode = ids.current[listboxState.folkused] && document.querySelector(`#${ids.current[listboxState.folkused]}`);

  const selectOption = (optionIndex: number) => {
    dispatch({ type: ActionTypes.select, payload: optionIndex });

    if (onSelect) {
      onSelect(optionIndex);
    }
  };

  const ifArrowUp = handleArrowUp(e => {
    e.preventDefault();
    if (listboxState.folkused > 0) {
      dispatch({ type: ActionTypes.folkus, payload: listboxState.folkused - 1 });
      if (folkusedNode) {
        scrollIntoView(folkusedNode, {scrollMode: 'if-needed', behavior: 'smooth', block: 'end', inline: 'nearest'});
      }
    }
  });

  const ifArrowDown = handleArrowDown(e => {
    e.preventDefault();
    if (listboxState.folkused < (items.length - 1)) {
      dispatch({ type: ActionTypes.folkus, payload: listboxState.folkused + 1 });
      if (folkusedNode) {
        scrollIntoView(folkusedNode, {scrollMode: 'if-needed', behavior: 'smooth', block: 'start', inline: 'nearest'});
      }
    }
  });

  const ifEnter = handleEnter(e => {
    e.preventDefault();
    selectOption(listboxState.folkused);
  });

  const keydownHandler = (e: React.KeyboardEvent) => {
    ifArrowUp(e);
    ifArrowDown(e);
    ifEnter(e);
  };

  // merge passed props with "controlled" props
  const listboxOptionProps =
    (optionIndex: number, attrOverrides: React.HTMLAttributes<HTMLLIElement> = {}): React.HTMLAttributes<HTMLLIElement> => ({
    ...attrOverrides,
    id: ids.current[optionIndex],
    'aria-selected': listboxState.selected.includes(optionIndex) || undefined,
    role: 'option',
    onClick: (e: React.MouseEvent<HTMLLIElement>) => {
      selectOption(optionIndex);
      if (attrOverrides.onClick) {
        attrOverrides.onClick(e);
      }
    },
    onMouseEnter: (e: React.MouseEvent<HTMLLIElement>) => {
      dispatch({ type: ActionTypes.folkus, payload: optionIndex });
      if (attrOverrides.onMouseEnter) {
        attrOverrides.onMouseEnter(e);
      }
    },
  });

  return { listboxState, listboxOptionProps, keydownHandler };
};
